import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Image as ImageIcon, Upload, Trash2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";

/**
 * Admin — upload/replace the poster shown as a big banner on the landing,
 * success and member pages. Single active image, on/off toggle.
 */
export default function AdminBanner() {
    const nav = useNavigate();
    const fileRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState({ enabled: false, has_image: false });
    const [uploading, setUploading] = useState(false);

    const refresh = async () => {
        try {
            const { data } = await api.get("/admin/banner");
            setInfo(data);
        } catch (e) {
            if (e?.response?.status === 401) nav("/admin/login");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!localStorage.getItem("sc_admin_token")) {
            nav("/admin/login");
            return;
        }
        refresh();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const pickFile = () => fileRef.current?.click();

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("image", file);
            const { data } = await api.post("/admin/banner", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setInfo({ enabled: data.enabled, has_image: true, updated_at: data.updated_at });
            toast.success("Poster uploaded — now live on the site");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const toggle = async (enabled) => {
        try {
            await api.patch("/admin/banner/toggle", { enabled });
            setInfo((prev) => ({ ...prev, enabled }));
            toast.success(enabled ? "Banner turned ON" : "Banner turned OFF");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update");
        }
    };

    const remove = async () => {
        if (!window.confirm("Remove the current poster?")) return;
        try {
            await api.delete("/admin/banner");
            setInfo({ enabled: false, has_image: false });
            toast.success("Poster removed");
        } catch {
            toast.error("Could not remove");
        }
    };

    return (
        <div className="min-h-[100dvh] pb-24" data-testid="admin-banner-page">
            <header className="border-b border-emerald-line/60 bg-bg-secondary/60 backdrop-blur-md">
                <div className="max-w-4xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
                    <Link
                        to="/admin"
                        className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-secondary hover:text-gold"
                        data-testid="back-to-dashboard"
                    >
                        <ArrowLeft size={14} /> Dashboard
                    </Link>
                    <div className="flex items-center gap-2 text-gold text-[11px] tracking-[0.24em] uppercase">
                        <ImageIcon size={14} /> Promo Banner
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 lg:px-12 pt-16">
                <div className="label-eyebrow flex items-center gap-3">
                    <span className="w-8 h-px bg-gold" />
                    Landing · Success · Member pages
                </div>
                <h1 className="font-serif font-medium text-4xl md:text-5xl mt-4 leading-tight">
                    Promo <span className="italic text-gold">Poster</span>
                </h1>
                <p className="text-ink-secondary mt-4 max-w-xl leading-relaxed">
                    Upload a poster (event flyer, announcement, anything) and it shows as a
                    big banner right under the header on the landing, application-success
                    and member pages. Turn it on/off any time without re-uploading.
                </p>

                {loading ? (
                    <div className="mt-16 text-ink-secondary">Loading…</div>
                ) : (
                    <>
                        <div className="mt-10 glass-card rounded-sm p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="banner-status">
                            <div>
                                <div className="label-eyebrow">Status</div>
                                <div className="font-serif text-2xl mt-1 text-ink-primary">
                                    {info.has_image ? (info.enabled ? "Live on site" : "Uploaded — hidden") : "No poster uploaded"}
                                </div>
                            </div>
                            {info.has_image && (
                                <button
                                    onClick={() => toggle(!info.enabled)}
                                    data-testid="banner-toggle"
                                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[11px] tracking-[0.22em] uppercase border transition-all ${
                                        info.enabled
                                            ? "border-gold/50 text-gold bg-gold/10 hover:bg-gold/15"
                                            : "border-emerald-line text-ink-secondary hover:border-gold hover:text-gold"
                                    }`}
                                >
                                    {info.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
                                    {info.enabled ? "Showing — turn off" : "Hidden — turn on"}
                                </button>
                            )}
                        </div>

                        {info.has_image && (
                            <div className={`mt-8 rounded-sm border overflow-hidden bg-black transition-opacity ${info.enabled ? "border-gold/30" : "border-emerald-line/40 opacity-50"}`} data-testid="banner-preview">
                                <img
                                    src={`${API}/public/banner/image?v=${encodeURIComponent(info.updated_at || "")}`}
                                    alt="Current poster"
                                    className="w-full max-h-[420px] object-contain mx-auto"
                                />
                            </div>
                        )}

                        <div className="mt-10 flex flex-col sm:flex-row gap-3">
                            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="banner-file-input" />
                            <button
                                onClick={pickFile}
                                disabled={uploading}
                                data-testid="banner-upload-btn"
                                className="btn-gold inline-flex items-center justify-center gap-3 px-8 py-3.5 rounded-full font-medium tracking-wide disabled:opacity-60"
                            >
                                <Upload size={16} />
                                <span className="text-sm tracking-[0.18em] uppercase">
                                    {uploading ? "Uploading…" : info.has_image ? "Replace poster" : "Upload poster"}
                                </span>
                            </button>
                            {info.has_image && (
                                <button
                                    onClick={remove}
                                    data-testid="banner-remove-btn"
                                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-red-900/40 text-red-300 rounded-full hover:bg-red-950/30 text-[11px] tracking-[0.22em] uppercase transition-all"
                                >
                                    <Trash2 size={14} />
                                    Remove
                                </button>
                            )}
                        </div>

                        <div className="mt-8 text-ink-muted text-xs leading-relaxed max-w-xl">
                            Max 8 MB, any image format. Guests can dismiss the banner with the
                            × in the corner — it stays off for their current visit only.
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}
